import { SupplierUserRole } from '@choco-admin/types';
import { useCallback } from 'react';

import { useSsoPageRoleMappingCreateMutation } from '../../../generated';
import type { SsoRoleMappingCreateResult } from '../types';
import { mapToSsoRoleMapping } from '../utils';

export interface CreateSsoRoleMappingInput {
  connectionId: string;
  federatedRole: string;
  supplierId: string;
  chocoSupplierRole: SupplierUserRole;
}

export interface UseSsoRoleMappingCreateResult {
  createRoleMapping: (
    input: CreateSsoRoleMappingInput
  ) => Promise<SsoRoleMappingCreateResult>;
  isCreating: boolean;
}

/**
 * Adds one role mapping to a connection.
 *
 * No bot user is sent. The backend derives it from the supplier and returns it
 * on the mapping, so a supplier without one is refused as an invalid
 * `supplier.supplierId` rather than as a missing field of this form.
 *
 * `federatedRole` is trimmed here because the backend trims it too. Sending a
 * padded value would collide with the trimmed one already stored and come back
 * as an already-exists error that names a role the person cannot see.
 */
export const useSsoRoleMappingCreate = (): UseSsoRoleMappingCreateResult => {
  const [mutate, { loading }] = useSsoPageRoleMappingCreateMutation({
    refetchQueries: ['SsoPageRoleMappings'],
    awaitRefetchQueries: true,
  });

  const createRoleMapping = useCallback(
    async ({
      connectionId,
      federatedRole,
      supplierId,
      chocoSupplierRole,
    }: CreateSsoRoleMappingInput): Promise<SsoRoleMappingCreateResult> => {
      const result = await mutate({
        variables: {
          input: {
            connectionId,
            federatedRole: federatedRole.trim(),
            supplier: { supplierId, chocoSupplierRole },
          },
        },
      });

      // `mutate.errorPolicy` is 'all' app-wide, so a rejected mutation resolves
      // with `errors` instead of throwing.
      const [transportError] = result.errors ?? [];
      const response = result.data?.ssoRoleMappingCreate;

      if (!response) {
        return {
          kind: 'request',
          message:
            transportError?.message ?? 'The role mapping could not be saved.',
        };
      }

      switch (response.__typename) {
        case 'SsoRoleMapping':
          return {
            kind: 'success',
            roleMapping: mapToSsoRoleMapping(response),
          };
        case 'SsoRoleMappingAlreadyExists':
          return {
            kind: 'alreadyExists',
            message:
              response.message ?? 'This connection already maps that role.',
          };
        case 'NotFoundError':
          return {
            kind: 'notFound',
            message: response.message ?? 'The connection no longer exists.',
          };
        case 'InputValidationFailure':
          return {
            kind: 'validation',
            message: response.message ?? 'Check the details and try again.',
            invalidFields:
              response.invalidFields?.map((field) => ({
                fieldName: field.fieldName,
                reason: field.reason,
              })) ?? [],
          };
        case 'AuthorizationFailure':
          return {
            kind: 'authorization',
            message:
              response.message ?? 'Not authorized to change role mappings.',
          };
        default:
          return {
            kind: 'request',
            message: 'The role mapping could not be saved.',
          };
      }
    },
    [mutate]
  );

  return { createRoleMapping, isCreating: loading };
};
